/**
 * rosterAllocation.ts
 * Pure helpers for the per-week WO/quote staffing roster (PR B — D3 / D6).
 *
 *   - defaultRosterPercent  — the % a newly-added person starts at (mentor 25,
 *                             else 100), restoring the Wave-3-retired mentor
 *                             default (MENTOR_DEFAULT_PERCENT).
 *   - orderBySpareCapacity  — "suggest who's free" ordering (D6): least-loaded
 *                             first by weekly allocation %.
 *   - spareCapacityHint     — the "lots free / some room / at capacity / over"
 *                             annotation for a candidate's current week %.
 *
 * No React / MUI / theme — colors + rendering live in the components.
 */

import { MENTOR_DEFAULT_PERCENT, FULL_ALLOCATION_PERCENT } from './scheduleAssignmentDefaults';

/** Skill level at or above which a person can act as an L3+ mentor. */
export const MENTOR_SKILL_LEVEL = 3;

/** A person already on the source's roster for the week (skill-level aware). */
export interface RosterMember {
  userKey: string;
  /** SkillMatrix level (1–5) for the task type, null/undefined when unrated. */
  skillLevel?: number | null;
}

/**
 * The default allocation % for a person just added to a WO/quote-week roster.
 *
 * Mentor heuristic (matches the retired scheduleAssignmentDefaults behaviour):
 * an L3+ person added to a week that ALREADY has an L1/L2 present is being
 * added to satisfy the skill gate — they supervise, so they default to
 * MENTOR_DEFAULT_PERCENT (25). Otherwise the person is doing the hands-on work
 * and defaults to 100.
 *
 * @param addedSkillLevel  The added person's skill level (1–5) or null.
 * @param existingMembers  People already on this source-week roster.
 */
export function defaultRosterPercent(
  addedSkillLevel: number | null | undefined,
  existingMembers: readonly RosterMember[],
): number {
  const isMentorLevel =
    typeof addedSkillLevel === 'number' && addedSkillLevel >= MENTOR_SKILL_LEVEL;
  if (!isMentorLevel) return FULL_ALLOCATION_PERCENT;
  const hasJunior = existingMembers.some(
    (m) => typeof m.skillLevel === 'number' && m.skillLevel > 0 && m.skillLevel < MENTOR_SKILL_LEVEL,
  );
  return hasJunior ? MENTOR_DEFAULT_PERCENT : FULL_ALLOCATION_PERCENT;
}

/** A candidate for the "suggest who's free" ordering. */
export interface SpareCandidate {
  userKey: string;
  displayName: string;
  /** Σ weekly allocation % for the target week (0 when free / unknown). */
  allocationPercent?: number | null;
}

/** Normalize a possibly-missing percent to a finite number ≥ 0. */
function pct(v: number | null | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * "Suggest who's free" (D6): order candidates least-loaded first by their
 * weekly allocation %, name as the stable tie-break. Returns a NEW array —
 * the input is untouched (memo-friendly). Advisory only: an over-loaded person
 * is never removed, just sorted last.
 */
export function orderBySpareCapacity<T extends SpareCandidate>(candidates: readonly T[]): T[] {
  return [...candidates].sort((a, b) => {
    const diff = pct(a.allocationPercent) - pct(b.allocationPercent);
    if (diff !== 0) return diff;
    return a.displayName.localeCompare(b.displayName);
  });
}

/** The spare-capacity phrase shown next to a candidate's current week %. */
export function spareCapacityHint(allocationPercent: number | null | undefined): string {
  const p = pct(allocationPercent);
  if (p > 100) return 'over';
  if (p >= 86) return 'at capacity';
  if (p >= 50) return 'some room';
  if (p > 0) return 'lots free';
  return 'free';
}
